import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { useWallet, type TokenSym } from "@/hooks/use-wallet";
import { useModalA11y } from "@/hooks/use-modal-a11y";

import sfnMark from "@/assets/ecosystem/sfn-mark.png.asset.json";
import meal2 from "@/assets/ecosystem/meal2.jpeg.asset.json";
import meal4 from "@/assets/ecosystem/meal4.jpeg.asset.json";
import plantyMark from "@/assets/ecosystem/plantypando.jpeg.asset.json";
import aquaMark from "@/assets/ecosystem/aquapando.jpeg.asset.json";
import solarMark from "@/assets/ecosystem/solarpando.png.asset.json";
import villageMark from "@/assets/ecosystem/villagepando.jpeg.asset.json";

export type EcosystemNodeId =
  | "sfn"
  | "meal2"
  | "meal4"
  | "planty"
  | "aqua"
  | "solar"
  | "village";

type NodeDef = {
  id: EcosystemNodeId;
  name: string;
  role: string;
  img: string;
  href: string;
  token?: TokenSym;
  external?: boolean;
};

export const ECOSYSTEM_NODES: NodeDef[] = [
  { id: "sfn", name: "SFN", role: "Sustainable Food & Eco Network", img: sfnMark.url, href: "/" },
  { id: "meal2", name: "MEAL2", role: "Daily Utility Circulation", img: meal2.url, href: "/#tokenomics", token: "MEAL2" },
  { id: "meal4", name: "MEAL4", role: "Food-Backed Reserve", img: meal4.url, href: "/#tokenomics", token: "MEAL4" },
  { id: "planty", name: "PlantyPando", role: "Agriculture Asset Node", img: plantyMark.url, href: "/villagepando/planty", token: "PLANTY-PANDO" },
  { id: "aqua", name: "AquaPando", role: "Water Systems Node", img: aquaMark.url, href: "/villagepando/aqua", token: "AQUA-PANDO" },
  { id: "solar", name: "SolarPando", role: "Energy Asset Node", img: solarMark.url, href: "/villagepando/solar", token: "SOLAR-PANDO" },
  { id: "village", name: "VillagePando", role: "Community RWA Cockpit", img: villageMark.url, href: "/villagepando", token: "VILLAGE-PANDO" },
];

const EMERALD = "#10B981";

/** Locale-independent balance formatting (avoids SSR/client ICU mismatches). */
function formatBalance(n: number): string {
  const [int, dec] = (Math.round(n * 100) / 100).toFixed(2).split(".");
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${dec}`;
}

function NodeBadge({
  node,
  active,
  size = 40,
  onClick,
}: {
  node: NodeDef;
  active: boolean;
  size?: number;
  onClick?: (n: NodeDef) => void;
}) {
  const px = `${size}px`;
  return (
    <button
      type="button"
      onClick={() => onClick?.(node)}
      aria-label={`${node.name} — ${node.role}`}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative shrink-0 overflow-hidden rounded-full border transition-all duration-300",
        "border-white/10 bg-[#0B0F17] hover:border-white/30 hover:scale-105",
        active && "z-10 border-transparent",
      )}
      style={{
        width: px,
        height: px,
        boxShadow: active
          ? `0 0 0 2px ${EMERALD}, 0 0 24px ${EMERALD}80, inset 0 0 0 1px rgba(255,255,255,0.12)`
          : "inset 0 0 0 1px rgba(255,255,255,0.06)",
      }}
    >
      <img
        src={node.img}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />
    </button>
  );
}

/**
 * Compact ecosystem switcher for header nav.
 * Renders SolarPando · PlantyPando · AquaPando · VillagePando · SFN Cockpit
 */
export function EcosystemSwitcher({
  activeId,
  className,
}: {
  activeId?: EcosystemNodeId;
  className?: string;
}) {
  const order: EcosystemNodeId[] = ["solar", "planty", "aqua", "village", "sfn"];
  const nodes = useMemo(
    () => order.map((id) => ECOSYSTEM_NODES.find((n) => n.id === id)!).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return (
    <div
      className={cn(
        "hidden xl:flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0B0F17]/70 px-2 py-1 backdrop-blur-md",
        className,
      )}
      role="navigation"
      aria-label="Ecosystem switcher"
    >
      {nodes.map((n) => {
        const label = n.id === "sfn" ? "SFN Cockpit" : n.name;
        const active = activeId === n.id;
        const content = (
          <span className="flex items-center gap-1.5 rounded-full px-1.5 py-1">
            <span
              className="grid place-items-center overflow-hidden rounded-full border border-white/10"
              style={{
                width: 18,
                height: 18,
                boxShadow: active ? `0 0 0 1.5px ${EMERALD}, 0 0 10px ${EMERALD}80` : undefined,
              }}
            >
              <img src={n.img} alt="" className="h-full w-full object-cover" loading="lazy" />
            </span>
            <span
              className={cn(
                "font-mono text-[10px] uppercase tracking-[0.14em] transition-colors",
                active ? "text-emerald-300" : "text-muted-foreground group-hover:text-foreground",
              )}
            >
              {label}
            </span>
          </span>
        );
        const cls = "group inline-flex items-center transition-colors";
        return n.href.startsWith("/") && !n.href.includes("#") ? (
          <Link
            key={n.id}
            to={n.href}
            onClick={() => track("cta_click", { id: `eco_switch_${n.id}`, surface: "nav" })}
            className={cls}
          >
            {content}
          </Link>
        ) : (
          <a
            key={n.id}
            href={n.href}
            onClick={() => track("cta_click", { id: `eco_switch_${n.id}`, surface: "nav" })}
            className={cls}
          >
            {content}
          </a>
        );
      })}
    </div>
  );
}

/**
 * Full horizontal Ecosystem Hub bar — 7 asset badges.
 * Renders the active node with a glowing emerald ring.
 * MEAL2 / MEAL4 open a balance drawer; other nodes navigate.
 */
export function EcosystemHub({
  activeId,
  className,
  title = "Ecosystem Hub",
  subtitle = "Seven interconnected assets · one sustainable network",
}: {
  activeId?: EcosystemNodeId;
  className?: string;
  title?: string;
  subtitle?: string;
}) {
  const [drawer, setDrawer] = useState<NodeDef | null>(null);
  const wallet = useWallet();

  const handleClick = (n: NodeDef) => {
    track("cta_click", { id: `eco_hub_${n.id}`, surface: "footer" });
    if (n.id === "meal2" || n.id === "meal4") {
      setDrawer(n);
      return;
    }
    if (n.href.startsWith("http")) {
      window.open(n.href, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = n.href;
    }
  };

  return (
    <section
      className={cn(
        "relative rounded-3xl border border-white/10 bg-[#0B0F17]/80 p-5 backdrop-blur-md sm:p-6",
        className,
      )}
      aria-label="Ecosystem Hub"
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-foreground">
            {title}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_#10B981]" />
          Solana · Live
        </span>
      </div>
      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:gap-5">
        {ECOSYSTEM_NODES.map((n) => (
          <div
            key={n.id}
            className="flex snap-start flex-col items-center gap-2"
            style={{ minWidth: 84 }}
          >
            <NodeBadge node={n} active={activeId === n.id} size={56} onClick={handleClick} />
            <div className="text-center">
              <div
                className={cn(
                  "font-mono text-[10px] uppercase tracking-[0.14em]",
                  activeId === n.id ? "text-emerald-300" : "text-foreground",
                )}
              >
                {n.name}
              </div>
              <div className="text-[10px] text-muted-foreground">{n.role}</div>
              {n.token && (
                <div className="mt-0.5 font-mono text-[10px] text-emerald-300/80">
                  {formatBalance(wallet.balances[n.token])}


                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {drawer && <BalanceDrawer node={drawer} onClose={() => setDrawer(null)} />}
    </section>
  );
}

function BalanceDrawer({ node, onClose }: { node: NodeDef; onClose: () => void }) {
  const wallet = useWallet();
  const bal = node.token ? wallet.balances[node.token] : 0;
  const dialogRef = useModalA11y({ open: true, onClose, analyticsId: `eco_balance_${node.id}` });
  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`${node.name} balance`}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0B0F17] p-6 shadow-[0_0_60px_#10B98140]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full border border-white/10 p-1.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3">
          <img
            src={node.img}
            alt=""
            className="h-14 w-14 rounded-full border border-white/10 object-cover"
          />
          <div>
            <div className="font-display text-lg font-semibold">{node.name}</div>
            <div className="text-xs text-muted-foreground">{node.role}</div>
          </div>
        </div>
        <div className="mt-6 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">
            Wallet Balance
          </div>
          <div className="mt-1 font-display text-3xl font-bold text-foreground">
            {bal.toLocaleString("en-US", { maximumFractionDigits: 2 })}
            <span className="ml-2 font-mono text-xs text-muted-foreground">{node.token}</span>
          </div>
          {!wallet.connected && (
            <button
              type="button"
              onClick={() => {
                onClose();
                wallet.openConnect();
              }}
              className="mt-3 w-full rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-xs font-bold uppercase tracking-wide text-emerald-300 hover:bg-emerald-400/20"
            >
              Connect Wallet
            </button>
          )}
        </div>
        <a
          href={node.href}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-obsidian hover:bg-emerald-400"
        >
          Open {node.name} →
        </a>
      </div>
    </div>
  );
}
